import { Injectable } from '@angular/core';
import { EnergySourceService } from '@backoffice-frontend/shared/bo/util-masterdata';
import {
    TimeSeriesFacade,
} from '@backoffice-frontend/time-series-identifier/api';
import type { TimeSeriesAnalysisIdentifierDataLocalized, TimeSeriesIdentifierInput } from '@clean-code/models';
import {
    EnergySource,
    EnergySourceEnum,
    EnergySourceLocalized,
    SpreadCollection, SpreadTable,
    TimeFrame,
} from '@clean-code/models';
import { ID, PagingResponse } from '@clean-code/models/common';
import { Dayjs } from 'dayjs';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { SpreadDataService } from './../infrastructure/spread.data.service';

@Injectable({ providedIn: 'root' })
export class IndividualSpreadsChartFacade {
  constructor(
    private energySourceService: EnergySourceService,
    private timeSeriesDataService: TimeSeriesFacade,
    private spreadDataService: SpreadDataService,
  ) {}

  getEnergySources$(): Observable<EnergySource[]> {
    return this.energySourceService
      .getAll$()
      .pipe(map((response: PagingResponse<EnergySource>) => response.items));
  }

  getEnergySourcesLocalized$(): Observable<EnergySourceLocalized[]> {
    return this.energySourceService
      .getAllLocalized$()
      .pipe(
        map(
          (response: PagingResponse<EnergySourceLocalized>) => response.items,
        ),
      );
  }
  getByEnergySource$(energySource: EnergySourceEnum) {
    return this.spreadDataService
      .getAllMarketAreasByEnergySource$(energySource)
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));
  }
  timeSeriesIdentifierDataByEnergySource$(
    energySourceId: ID,
  ): Observable<TimeSeriesAnalysisIdentifierDataLocalized[]> {
    return this.timeSeriesDataService.timeSeriesIdentifierDataByEnergySource$(
      energySourceId,
    );
  }

  timeSeriesIdentifierDataByMarketArea$(
    areaId: ID,
    from: Dayjs = null,
    to: Dayjs = null,
  ): Observable<TimeSeriesAnalysisIdentifierDataLocalized[]> {
    return this.timeSeriesDataService.timeSeriesIdentifierDataByMarketArea$(
      areaId,
      from,
      to,
    );
  }

  getAll$(
    symbols: TimeSeriesAnalysisIdentifierDataLocalized[],
    timeFrame: TimeFrame,
  ): Observable<SpreadCollection> {
    return this.spreadDataService.getAll$(symbols, timeFrame);
  }

  getTable$(
    symbols: TimeSeriesAnalysisIdentifierDataLocalized[],
    timeFrame: TimeFrame,
  ): Observable<SpreadTable> {
    return this.spreadDataService.getTable$(symbols, timeFrame);
  }

  withDynamicInput$(
    symbols: TimeSeriesIdentifierInput[],
    timeFrame: TimeFrame,
  ): Observable<SpreadCollection> {
    return this.spreadDataService.withDynamicInput$(symbols, timeFrame);
  }
}
